import type {
  ProjectBriefContent,
  ProjectBriefRecord,
  ProjectBriefSectionTarget,
} from "../types/projectBrief";

interface ProjectBriefResponse {
  ok?: boolean;
  brief?: ProjectBriefRecord;
  error?: string;
}

async function parseJsonSafely(response: Response): Promise<ProjectBriefResponse | null> {
  if (response.status === 204) {
    return null;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as ProjectBriefResponse;
  } catch (error) {
    console.error("Failed to parse project brief response", error);
    return null;
  }
}

export async function fetchProjectBrief(projectId: string): Promise<ProjectBriefRecord | null> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/brief`);

  if (response.status === 404 || response.status === 204) {
    return null;
  }

  const payload = await parseJsonSafely(response);

  if (!response.ok) {
    throw new Error(payload?.error ?? `Failed to load project brief (status ${response.status})`);
  }

  if (!payload?.brief) {
    return null;
  }

  return payload.brief;
}

export async function generateProjectBrief(projectId: string): Promise<ProjectBriefRecord> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/brief/generate`, {
    method: "POST",
  });

  const payload = await parseJsonSafely(response);

  if (!response.ok || !payload?.brief) {
    throw new Error(payload?.error ?? `Failed to generate project brief (status ${response.status})`);
  }

  return payload.brief;
}

export async function updateProjectBrief(
  projectId: string,
  content: ProjectBriefContent,
): Promise<ProjectBriefRecord> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/brief`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
  });

  const payload = await parseJsonSafely(response);

  if (!response.ok || !payload?.brief) {
    throw new Error(payload?.error ?? `Failed to update project brief (status ${response.status})`);
  }

  return payload.brief;
}

export interface RegenerateProjectBriefSectionInput {
  target: ProjectBriefSectionTarget;
  sectionId?: string;
  markRegenerated?: boolean;
}

export async function regenerateProjectBriefSection(
  projectId: string,
  target: RegenerateProjectBriefSectionInput,
): Promise<ProjectBriefRecord> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  if (!target?.target) {
    throw new Error("target is required");
  }

  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/brief/regenerate-section`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(target),
  });

  const payload = await parseJsonSafely(response);

  if (!response.ok || !payload?.brief) {
    throw new Error(payload?.error ?? `Failed to regenerate project brief section (status ${response.status})`);
  }

  return payload.brief;
}

export async function approveProjectBrief(projectId: string): Promise<ProjectBriefRecord> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/brief/approve`, {
    method: "POST",
  });

  const payload = await parseJsonSafely(response);

  if (!response.ok || !payload?.brief) {
    throw new Error(payload?.error ?? `Failed to approve project brief (status ${response.status})`);
  }

  return payload.brief;
}

export async function exportProjectBrief(
  projectId: string,
  format: "markdown" | "pdf",
): Promise<{ blob: Blob; filename: string }> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  const response = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/brief/export?format=${encodeURIComponent(format)}`,
  );

  if (!response.ok) {
    const payload = await parseJsonSafely(response);
    throw new Error(payload?.error ?? `Failed to export project brief (status ${response.status})`);
  }

  const disposition = response.headers.get("content-disposition") ?? "";
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch?.[1] ?? `project-brief-${projectId}.${format === "pdf" ? "pdf" : "md"}`;

  const blob = await response.blob();

  return { blob, filename };
}
